import { Roles } from "@/utilities/types/Users";
import SignInForm from "./sign-in-form";
import AgentSignInForm from "./agent-sign-in-form";
import { Turnstile, TurnstileInstance } from "@marsidev/react-turnstile";
import AuthRepository from "@/utilities/repositories/Auths";
import { useRef, useState } from "react";
import { CLOUDFLARE_TURNSTILE_SITE_KEY } from "@/config/constants";
import toast from "react-hot-toast";

export interface SignInFormRendererProps {
  signInAs: Roles;
}
function SignInFormRenderer({ signInAs }: SignInFormRendererProps) {
  const [loadingRecaptcha, setLoadingRecaptcha] = useState(true);
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const [isRecaptchaVerified, setIsRecaptchaVerified] = useState(false);
  const [hasRecaptchaError, setHasRecaptchaError] = useState(false);
  const lastRecaptchaErrorToastAt = useRef(0);

  const notifyRecaptchaError = (message: string) => {
    const now = Date.now();

    if (now - lastRecaptchaErrorToastAt.current < 10000) return;

    lastRecaptchaErrorToastAt.current = now;
    toast.error(message);
  };

  const onRecaptchaError = (errorCode: string) => {
    console.error("Cloudflare Turnstile challenge failed", {
      errorCode,
      hostname: window.location.hostname,
      online: navigator.onLine,
      userAgent: navigator.userAgent,
    });
    setIsRecaptchaVerified(false);
    setHasRecaptchaError(true);
    setLoadingRecaptcha(false);
    notifyRecaptchaError("Verification failed. Please try again.");
  };

  const onRecaptchaUnsupported = () => {
    console.error("Cloudflare Turnstile is unsupported in this browser", {
      hostname: window.location.hostname,
      userAgent: navigator.userAgent,
    });
    setIsRecaptchaVerified(false);
    setHasRecaptchaError(true);
    setLoadingRecaptcha(false);
    notifyRecaptchaError(
      "This browser cannot complete verification. Please use an updated browser.",
    );
  };

  const onRecaptchaTimeout = () => {
    console.warn("Cloudflare Turnstile challenge timed out", {
      hostname: window.location.hostname,
      online: navigator.onLine,
    });
    setIsRecaptchaVerified(false);
    setHasRecaptchaError(false);
    setLoadingRecaptcha(true);
    notifyRecaptchaError("Verification took too long. Retrying...");
  };

  const retryRecaptcha = () => {
    setHasRecaptchaError(false);
    setIsRecaptchaVerified(false);
    setLoadingRecaptcha(true);
    turnstileRef.current?.reset();
  };

  const onVerifyRecaptcha = async (token: string) => {
    setLoadingRecaptcha(true);
    try {
      await AuthRepository.verifyRecaptcha(token);
      setIsRecaptchaVerified(true);
      setHasRecaptchaError(false);
      setLoadingRecaptcha(false);
    } catch (error) {
      console.error("Error during recaptcha verification", error);
      toast.error("Recaptcha verification failed");
      turnstileRef.current?.reset();
      setLoadingRecaptcha(true);
      setIsRecaptchaVerified(false);
      setHasRecaptchaError(true);
    }
  };

  return (
    <>
      {signInAs === Roles.ADMIN && (
        <SignInForm
          loading={loadingRecaptcha}
          isRecaptchaVerified={isRecaptchaVerified}
        />
      )}
      {signInAs === Roles.AGENCY && (
        <AgentSignInForm
          loading={loadingRecaptcha}
          isRecaptchaVerified={isRecaptchaVerified}
        />
      )}

      <Turnstile
        ref={turnstileRef}
        siteKey={CLOUDFLARE_TURNSTILE_SITE_KEY}
        onSuccess={onVerifyRecaptcha}
        onError={onRecaptchaError}
        onUnsupported={onRecaptchaUnsupported}
        onTimeout={onRecaptchaTimeout}
        options={{
          retry: "auto",
          refreshExpired: "auto",
          refreshTimeout: "auto",
        }}
        onExpire={() => {
          setIsRecaptchaVerified(false);
          setHasRecaptchaError(false);
          setLoadingRecaptcha(true);
        }}
      />
      {hasRecaptchaError && (
        <button
          type="button"
          className="mt-2 text-sm font-medium text-blue-600 underline"
          onClick={retryRecaptcha}
        >
          Try verification again
        </button>
      )}
    </>
  );
}

export default SignInFormRenderer;
